import axios, {
  AxiosError,
  AxiosHeaders,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { jwtDecode } from "jwt-decode";
import { useEffect, useMemo, useRef } from "react";
import {
  HEALTH_CHECK_TRIGGER_EVENT,
  HEALTH_TRIGGER_DEBOUNCE_MS,
} from "components/common/ErrorHandler/ErrorHandler.constants";
import { ENVS, USER_API } from "config";
import { isMaintenanceApiResponse } from "utils/isMaintenanceApiResponse";
import { useAuth } from "store/hooks/useAuth";
import useAuthToken from "./useAuthToken";
import { isJwtToken } from "lib/better-auth-session";
import { getDeviceHeaders, shouldAttachDeviceHeadersForPath } from "utils/deviceMetadata";
import { shouldStripAuthHeaderOnPublicRequest } from "utils/axiosPublicAuthPolicy";
import { captureAxiosErrorForPosthog } from "utils/captureAxiosErrorForPosthog";
import {
  AuthSessionChangedError,
  captureAuthSession,
  isAuthSessionCurrent,
  type AuthSessionOwner,
} from "lib/auth-session-ownership";

type AuthenticatedRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
  _authSession?: AuthSessionOwner | null;
};

type AxiosOptions = {
  timeout?: number;
  allResponse?: boolean;
  headers?: Record<string, string>;
  /**
   * Additional public endpoints (absolute URLs or pathnames like "/auth/login").
   * By default, these are merged with the built-in public endpoints.
   */
  publicEndpoints?: readonly string[];
  /**
   * Controls how `publicEndpoints` is applied.
   * - "merge" (default): add to the built-in list
   * - "replace": use ONLY `publicEndpoints`
   */
  publicEndpointsMode?: "merge" | "replace";
  /**
   * Called right before we log the user out after an unrecoverable auth failure (usually 401).
   * Use this to record telemetry / show a toast / open a modal, etc.
   */
  onAuthFailure?: (context: AuthFailureContext) => void;
};

type JwtPayload = { exp?: number };

const AUTH_HEADER = "Authorization";
const CONTENT_TYPE = "Content-Type";
const BEARER = "Bearer";

let lastHealthTriggerAt = 0;

function maybeDispatchHealthCheckTrigger() {
  if (typeof window === "undefined") return;
  const now = Date.now();
  if (now - lastHealthTriggerAt < HEALTH_TRIGGER_DEBOUNCE_MS) return;
  lastHealthTriggerAt = now;
  window.dispatchEvent(new CustomEvent(HEALTH_CHECK_TRIGGER_EVENT));
}

type AuthFailureContext = {
  statusCode: number;
  requestUrl: string;
  requestPath: string | null;
  isPublic: boolean;
  isNonAuthEndpoint: boolean;
  isPasswordUpdate: boolean;
  didRetry: boolean;
  responseData: unknown;
};

type E2EWindow = Window & {
  __E2E__?: {
    ignore401?: boolean;
  };
};

function safePathFromUrl(rawUrl: string | undefined, baseURL: string): string | null {
  if (!rawUrl) return null;
  try {
    const u = new URL(rawUrl, baseURL);
    const base = new URL(baseURL);
    // Only treat same-origin requests as part of our API surface
    if (u.origin !== base.origin) return null;
    return u.pathname;
  } catch {
    // If URL construction fails (e.g. invalid baseURL), fall back to naive path parsing
    const withoutQuery = rawUrl.split("?")[0];
    if (!withoutQuery) return null;
    if (withoutQuery.startsWith("/")) return withoutQuery;
    return `/${withoutQuery}`;
  }
}

function normalizeEndpointToPath(endpoint: string, baseURL: string): string | null {
  if (!endpoint) return null;
  const trimmed = endpoint.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith("/")) return trimmed.split("?")[0] || null;
  return safePathFromUrl(trimmed, baseURL);
}

/** Public read for shared scripts: no auth; 401 must not trigger refresh/logout for viewers. */
function isPublicShareScriptReadPath(pathname: string | null): boolean {
  return Boolean(pathname?.startsWith("/share/scripts/"));
}

function isTokenExpiringSoon(jwt: string, bufferSeconds = 120): boolean {
  if (!jwt) return true;
  if (!isJwtToken(jwt)) return false;
  try {
    const decoded = jwtDecode<JwtPayload>(jwt);
    const exp = decoded?.exp;
    if (typeof exp !== "number") return true;
    const now = Date.now() / 1000;
    return exp < now + bufferSeconds;
  } catch {
    return true;
  }
}

function applyDeviceHeaders(cfg: InternalAxiosRequestConfig) {
  const deviceHeaders = getDeviceHeaders();
  if (!cfg.headers) cfg.headers = new AxiosHeaders();
  if (cfg.headers instanceof AxiosHeaders) {
    for (const [key, value] of Object.entries(deviceHeaders)) {
      cfg.headers.set(key, value);
    }
    return;
  }

  const h = cfg.headers as Record<string, string>;
  Object.assign(h, deviceHeaders);
}

function removeAuthHeader(cfg: InternalAxiosRequestConfig) {
  if (!cfg.headers) return;
  if (cfg.headers instanceof AxiosHeaders) {
    cfg.headers.delete(AUTH_HEADER);
    return;
  }
  const h = cfg.headers as Record<string, unknown>;
  delete h[AUTH_HEADER];
}

function setAuthHeader(cfg: InternalAxiosRequestConfig, token: string) {
  if (!cfg.headers) cfg.headers = new AxiosHeaders();
  if (cfg.headers instanceof AxiosHeaders) {
    cfg.headers.set(AUTH_HEADER, `${BEARER} ${token}`);
    return;
  }
  const h = cfg.headers as Record<string, string>;
  h[AUTH_HEADER] = `${BEARER} ${token}`;
}

function getHeaderValue(headers: unknown, name: string): string | null {
  if (!headers) return null;
  if (headers instanceof AxiosHeaders) {
    const v = headers.get(name);
    return typeof v === "string" ? v : null;
  }
  const h = headers as Record<string, unknown>;
  const v = h[name];
  return typeof v === "string" ? v : null;
}

function bearerTokenFromAuthHeader(value: string | null): string | null {
  if (!value) return null;
  if (!value.startsWith(`${BEARER} `)) return null;
  const token = value.slice(`${BEARER} `.length).trim();
  return token ? token : null;
}

/**
 * The sign-in token at the moment a request is sent. Instances outlive their component, and
 * React Query keeps the instance of whichever observer built a query, so a token captured at
 * render time goes stale after sign-out and sign-in.
 */
export function useAxios({
  timeout = 10000,
  allResponse = false,
  headers,
  publicEndpoints,
  publicEndpointsMode = "merge",
  onAuthFailure,
}: AxiosOptions = {}) {
  const baseURL = ENVS.backendUrl;
  const { handleLogOut } = useAuth();
  const { refreshToken } = useAuthToken();

  const publicPaths = useMemo(() => {
    const defaultEndpoints = [
      USER_API.login,
      USER_API.register,
      USER_API.resendEmail,
      USER_API.forgotPassword,
      USER_API.resetPassword,
      USER_API.mfaCompleteLogin,
      USER_API.initiateSsoLoginUrl,
      USER_API.completeSsoLoginUrl,
      USER_API.initiateSocialOAuth("google"),
      USER_API.initiateSocialOAuth("linkedin"),
      USER_API.completeSocialOAuth("google"),
      USER_API.completeSocialOAuth("linkedin"),
      USER_API.oauthAvailability,
      USER_API.passkeyAuthenticateBegin,
      USER_API.passkeyAuthenticateComplete,
    ];

    const endpoints =
      publicEndpointsMode === "replace"
        ? [...(publicEndpoints || [])]
        : [...defaultEndpoints, ...(publicEndpoints || [])];

    return new Set(
      endpoints
        .map((u) => normalizeEndpointToPath(u, baseURL))
        .filter((p): p is string => typeof p === "string" && p.length > 0)
    );
  }, [baseURL, publicEndpoints, publicEndpointsMode]);

  const passwordUpdatePath = useMemo(
    () => safePathFromUrl(USER_API.passwordUpdate, baseURL),
    [baseURL]
  );
  const projectsPath = useMemo(() => safePathFromUrl(USER_API.projects, baseURL), [baseURL]);
  const projectQuotaPath = useMemo(
    () => safePathFromUrl(USER_API.getProjectQuota, baseURL),
    [baseURL]
  );

  // The interceptors read these through a ref, so they are attached once when the instance
  // is created and never ejected.
  const latestRef = useRef({
    publicPaths,
    passwordUpdatePath,
    projectsPath,
    projectQuotaPath,
    refreshToken,
    handleLogOut,
    allResponse,
    onAuthFailure,
  });
  useEffect(() => {
    latestRef.current = {
      publicPaths,
      passwordUpdatePath,
      projectsPath,
      projectQuotaPath,
      refreshToken,
      handleLogOut,
      allResponse,
      onAuthFailure,
    };
  });

  // An instance must work for its whole life, not only while its component is mounted.
  // The offline screen unmounts the app, and a query paused while offline resumes on
  // reconnect with the instance it started with. Interceptors added in an effect were
  // ejected by then, so it got the raw axios response instead of the body.
  const axiosInstance: AxiosInstance = useMemo(() => {
    const instance = axios.create({
      baseURL,
      timeout,
      headers: {
        [CONTENT_TYPE]: "application/json",
        ...(headers || {}),
      },
    });

    instance.interceptors.request.use(
      async (requestConfig) => {
        const cfg = requestConfig as AuthenticatedRequestConfig;
        const { publicPaths, refreshToken } = latestRef.current;
        const requestPath = safePathFromUrl(cfg.url, baseURL);
        if (shouldAttachDeviceHeadersForPath(requestPath)) {
          applyDeviceHeaders(cfg);
        }

        const isPublic =
          requestPath != null &&
          (publicPaths.has(requestPath) || isPublicShareScriptReadPath(requestPath));

        if (isPublic) {
          if (
            !isPublicShareScriptReadPath(requestPath) &&
            shouldStripAuthHeaderOnPublicRequest(requestPath)
          ) {
            removeAuthHeader(cfg);
          }
          return cfg;
        }

        // A header the caller set wins; otherwise use the token signed in right now.
        const explicitToken = bearerTokenFromAuthHeader(getHeaderValue(cfg.headers, AUTH_HEADER));
        const current = captureAuthSession();
        if (cfg._authSession && !isAuthSessionCurrent(cfg._authSession)) {
          throw new AuthSessionChangedError();
        }
        if (cfg._authSession === undefined) {
          cfg._authSession =
            current.token && (!explicitToken || explicitToken === current.token) ? current : null;
        }
        const owner = cfg._authSession;
        const bearerToken = explicitToken ?? (current.token.trim() || null);
        if (!explicitToken && bearerToken) setAuthHeader(cfg, bearerToken);

        if (owner && bearerToken && isTokenExpiringSoon(bearerToken)) {
          try {
            const newToken = await refreshToken();
            if (!isAuthSessionCurrent(owner)) throw new AuthSessionChangedError();
            if (newToken) {
              setAuthHeader(cfg, newToken);
              return cfg;
            }
          } catch {
            if (!isAuthSessionCurrent(owner)) throw new AuthSessionChangedError();
            // Fall back to existing token below
          }
        }
        return cfg;
      },
      (error) => Promise.reject(error)
    );

    instance.interceptors.response.use(
      (response: AxiosResponse) => {
        return latestRef.current.allResponse ? response : response.data;
      },
      async (error: AxiosError) => {
        const {
          publicPaths,
          passwordUpdatePath,
          projectsPath,
          projectQuotaPath,
          refreshToken,
          handleLogOut,
          onAuthFailure,
        } = latestRef.current;
        const originalRequest = (error.config || {}) as AuthenticatedRequestConfig;
        const reqPath = safePathFromUrl(originalRequest?.url, baseURL);

        if (error instanceof AuthSessionChangedError) return Promise.reject(error);
        if (!error.response) {
          maybeDispatchHealthCheckTrigger();
          captureAxiosErrorForPosthog(error, { requestPath: reqPath });
          return Promise.reject(error);
        }

        const statusCode = error.response.status;
        const responseData = error.response.data as { code?: string; status?: string } | undefined;
        if (isMaintenanceApiResponse(statusCode, responseData) && typeof window !== "undefined") {
          maybeDispatchHealthCheckTrigger();
          return Promise.reject(error);
        }

        captureAxiosErrorForPosthog(error, { requestPath: reqPath });

        const ignoreAuthFailure =
          typeof window !== "undefined" && Boolean((window as E2EWindow).__E2E__?.ignore401);
        const isPublic =
          reqPath != null && (publicPaths.has(reqPath) || isPublicShareScriptReadPath(reqPath));

        const isNonAuthEndpoint =
          Boolean(reqPath && projectsPath && reqPath.startsWith(projectsPath)) ||
          Boolean(reqPath && projectQuotaPath && reqPath.startsWith(projectQuotaPath));

        const isPasswordUpdate =
          Boolean(reqPath && passwordUpdatePath && reqPath === passwordUpdatePath) ||
          // Fallback for odd URL shapes
          Boolean(originalRequest?.url && originalRequest.url.endsWith(USER_API.passwordUpdate));

        if (statusCode === 401 && !isPublic && !isPasswordUpdate) {
          const owner = originalRequest._authSession;
          if (!owner || !isAuthSessionCurrent(owner)) return Promise.reject(error);
          // One retry after refresh
          if (!originalRequest._retry) {
            originalRequest._retry = true;
            try {
              const newToken = await refreshToken();
              if (!isAuthSessionCurrent(owner)) return Promise.reject(error);
              if (newToken) {
                originalRequest.headers = originalRequest.headers || new AxiosHeaders();
                if (originalRequest.headers instanceof AxiosHeaders) {
                  originalRequest.headers.set(AUTH_HEADER, `${BEARER} ${newToken}`);
                } else {
                  const h = originalRequest.headers as Record<string, string>;
                  h[AUTH_HEADER] = `${BEARER} ${newToken}`;
                }
                return instance(originalRequest);
              }
            } catch (refreshError) {
              if (refreshError instanceof AuthSessionChangedError) return Promise.reject(error);
              // Fall through to logout/propagate
            }
          }

          // Some endpoints should not force a full logout (and E2E can opt out).
          if (isNonAuthEndpoint || ignoreAuthFailure) {
            return Promise.reject(responseData || error);
          }

          if (!isAuthSessionCurrent(owner)) return Promise.reject(error);
          onAuthFailure?.({
            statusCode,
            requestUrl: originalRequest?.url || "",
            requestPath: reqPath,
            isPublic,
            isNonAuthEndpoint,
            isPasswordUpdate,
            didRetry: Boolean(originalRequest._retry),
            responseData: responseData as unknown,
          });

          if (!isAuthSessionCurrent(owner)) return Promise.reject(error);
          handleLogOut();
          return Promise.reject(error);
        }

        if (statusCode >= 400) {
          return Promise.reject(error);
        }

        return Promise.reject(error);
      }
    );

    return instance;
  }, [baseURL, timeout, headers]);

  return axiosInstance;
}

export const defaultAxios = axios;
